import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Upload, Video, AlertCircle, Loader2, Play, RotateCcw, ChevronDown, ChevronUp, Check, Copy } from 'lucide-react';
import { API_ROOT } from '@/config';
import SEO from '@/components/seo';
import MobileSheet from '@/mobile/components/ui/MobileSheet';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface CampaignInfo {
  campaign_uuid: string;
  campaign_name: string;
  brand_name: string;
  product_name: string;
  requirements: any;
  brand_logo_path?: string;
  product_image_path?: string;
}

interface VersionInfo {
  analysis_id: string;
  status: string;
  score: number | null;
  passing: boolean | null;
  created_at: string;
  version: number;
}

interface PendingAnalysis {
  analysis_id: string;
  status: string;
  score: number | null;
  passing: boolean | null;
  created_at: string;
}

interface CheckSubmissionResult {
  exists: boolean;
  version_count: number;
  next_version: number;
  all_versions: VersionInfo[];
  pending_analysis?: PendingAnalysis | null;
  can_analyze?: boolean;
  usage?: number;
  limit?: number;
}

const CreatorCampaignUploadMobile: React.FC = () => {
  const { campaign_uuid } = useParams<{ campaign_uuid: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Campaign info
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(true);
  const [campaignError, setCampaignError] = useState('');

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [step, setStep] = useState<'info' | 'upload' | 'preview' | 'pending' | 'analyzing'>('info');

  // Creator info - separate first/last name
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [creatorEmail, setCreatorEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Version checking
  const [checkResult, setCheckResult] = useState<CheckSubmissionResult | null>(null);
  const [isCheckingSubmission, setIsCheckingSubmission] = useState(false);
  const [showVersions, setShowVersions] = useState(false);

  // Limit check
  const [canAnalyze, setCanAnalyze] = useState(true);

  // Briefing sheet state
  const [showBriefingSheet, setShowBriefingSheet] = useState(false);
  const [copiedBriefing, setCopiedBriefing] = useState(false);

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  // Fetch campaign info
  useEffect(() => {
    const fetchCampaign = async () => {
      if (!campaign_uuid) return;

      try {
        const response = await fetch(`${API_ROOT}/campaigns/${campaign_uuid}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setCampaignError('Campaign not found');
          } else {
            setCampaignError('Failed to load campaign');
          }
          return;
        }

        const data = await response.json();
        setCampaignInfo(data);
      } catch (err) {
        setCampaignError('Failed to load campaign');
      } finally {
        setIsLoadingCampaign(false);
      }
    };

    fetchCampaign();
  }, [campaign_uuid]);

  // Validate email format
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Check for existing submissions
  const handleCheckSubmission = async () => {
    if (!firstName.trim() || !surname.trim()) {
      setError('Please enter your first name and surname');
      return;
    }
    if (!creatorEmail.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!isValidEmail(creatorEmail.trim())) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setError('');
    setEmailError('');
    setIsCheckingSubmission(true);

    try {
      const response = await fetch(
        `${API_ROOT}/campaigns/${campaign_uuid}/check-submission?creator_email=${encodeURIComponent(creatorEmail.trim())}`
      );

      if (!response.ok) {
        // If endpoint fails, just proceed to upload
        setStep('upload');
        return;
      }

      const data: CheckSubmissionResult = await response.json();
      setCheckResult(data);
      setCanAnalyze(data.can_analyze !== false);

      if (data.can_analyze === false) {
        setIsCheckingSubmission(false);
        return;
      }

      // If there's a pending analysis, show the pending step
      if (data.pending_analysis) {
        setStep('pending');
        return;
      }

      // Otherwise, proceed to upload
      setStep('upload');
    } catch (err) {
      // If check fails, just proceed to upload
      setStep('upload');
    } finally {
      setIsCheckingSubmission(false);
    }
  };

  // Handle continuing with pending analysis
  const handleContinuePending = () => {
    if (checkResult?.pending_analysis?.analysis_id) {
      const campaignParam = campaign_uuid ? `?campaign=${campaign_uuid}` : '';
      navigate(`/campaign-result/${checkResult.pending_analysis.analysis_id}${campaignParam}`);
    }
  };

  // Handle discarding pending and uploading new
  const handleDiscardPending = () => {
    setStep('upload');
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      setError('Please select a video file');
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      setError('Video must be less than 500MB');
      return;
    }

    setSelectedFile(file);
    setVideoPreviewUrl(URL.createObjectURL(file));
    setStep('preview');
    setError('');
  };

  // Start analysis
  const handleAnalyze = async () => {
    if (!selectedFile || !campaign_uuid) return;

    setStep('analyzing');
    setIsAnalyzing(true);
    setProgress(0);
    setError('');

    try {
      const sessionId = `mobile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('campaign_uuid', campaign_uuid);
      formData.append('session_id', sessionId);
      formData.append('creator_email', creatorEmail.trim());
      formData.append('creator_first_name', firstName.trim());
      formData.append('creator_surname', surname.trim());
      formData.append('creator_name', `${firstName.trim()} ${surname.trim()}`);

      const DURATION_MS = 90_000;
      const TICK_MS = 250;
      const startTime = Date.now();
      const timerInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        setProgress(Math.min(Math.round((elapsed / DURATION_MS) * 100), 100));
      }, TICK_MS);

      console.log('[Mobile] Sending analysis request to /analyze-campaign-video with campaign_uuid:', campaign_uuid);
      const response = await fetch(`${API_ROOT}/analyze-campaign-video`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(timerInterval);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Mobile] Analysis failed:', response.status, errorText);
        let detail = 'Analysis failed';
        try { detail = JSON.parse(errorText).detail || detail; } catch {}
        throw new Error(detail);
      }

      const result = await response.json();
      console.log('[Mobile] Analysis result received:', {
        unique_id: result.unique_id,
        compliance_score: result.compliance_score,
        passing: result.passing,
        compliant_count: result.compliant?.length,
        non_compliant_count: result.non_compliant?.length,
        compliant_type: result.compliant?.[0] ? typeof result.compliant[0] : 'empty',
        non_compliant_type: result.non_compliant?.[0] ? typeof result.non_compliant[0] : 'empty',
      });
      setProgress(100);
      // Navigate directly to the full result page (single step, no intermediate screen)
      if (result.unique_id) {
        const campaignParam = campaign_uuid ? `?campaign=${campaign_uuid}` : '';
        navigate(`/campaign-result/${result.unique_id}${campaignParam}`);
      } else {
        // Fallback: if no unique_id, show error
        throw new Error('Analysis completed but no result ID was returned');
      }
    } catch (err: any) {
      setError(err.message || 'Analysis failed. Please try again.');
      setStep('preview');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const buildBriefingText = (): string => {
    if (!campaignInfo) return '';
    const lines: string[] = [];
    lines.push(`Campaign Briefing: ${campaignInfo.campaign_name}`);
    if (campaignInfo.brand_name) lines.push(`Brand: ${campaignInfo.brand_name}`);
    if (campaignInfo.product_name) lines.push(`Product: ${campaignInfo.product_name}`);
    lines.push('');

    const req = campaignInfo.requirements;
    if (!req) return lines.join('\n');

    if (Array.isArray(req)) {
      lines.push('Requirements:');
      req.filter((r: any) => r && typeof r === 'string' && r.trim()).forEach((r: string) => lines.push(`  • ${r}`));
    } else if (typeof req === 'object') {
      const names: Record<string, string> = {
        hooks: 'Hooks', topics: 'Topics', call_to_actions: 'Call to Actions',
        elements: "Don'ts", story: 'Story', visuals_background: 'Visuals & Background',
      };
      const order = ['hooks', 'topics', 'call_to_actions', 'elements', 'story', 'visuals_background'];
      for (const key of order) {
        const val = req[key];
        if (Array.isArray(val)) {
          const items = val.filter((i: any) => i && typeof i === 'string' && i.trim());
          if (items.length) {
            lines.push(`${names[key] || key}:`);
            items.forEach((i: string) => lines.push(`  • ${i}`));
            lines.push('');
          }
        } else if (typeof val === 'string' && val.trim()) {
          lines.push(`${names[key] || key}:`);
          lines.push(`  ${val}`);
          lines.push('');
        }
      }
    }
    return lines.join('\n');
  };

  const handleCopyBriefing = async () => {
    try {
      await navigator.clipboard.writeText(buildBriefingText());
      setCopiedBriefing(true);
      setTimeout(() => setCopiedBriefing(false), 2000);
    } catch {
      console.error('Failed to copy briefing');
    }
  };

  // Reset to try again
  const handleReset = () => {
    setSelectedFile(null);
    setVideoPreviewUrl(null);
    setProgress(0);
    setStep('upload');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // No back arrow in the header — the wizard guides the user forward through the flow

  // Loading state
  if (isLoadingCampaign) {
    return (
      <div className="mobile-app" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="mobile-spinner" />
        <p style={{ marginTop: 16, color: '#666' }}>Loading campaign...</p>
      </div>
    );
  }

  // Error state
  if (campaignError) {
    return (
      <div className="mobile-app" style={{ justifyContent: 'center', alignItems: 'center', padding: 24 }}>
        <AlertCircle size={48} color="#dc2626" />
        <h2 style={{ marginTop: 16, color: PALETTE.gunmetal }}>{campaignError}</h2>
        <p style={{ color: '#666', textAlign: 'center' }}>
          This campaign link may be invalid or expired.
        </p>
      </div>
    );
  }

  return (
    <div className="mobile-app">
      <SEO
        title="Upload Your Video"
        description="Upload your video to check compliance against the campaign brief. BrandComply helps creators submit on-brand content."
        path="/upload"
      />
      {/* Header */}
      <div style={{ 
        background: PALETTE.gunmetal, 
        padding: '16px 16px 24px',
        paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h1 style={{ color: '#fff', fontSize: 20, margin: 0 }}>
              {campaignInfo?.campaign_name}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <p style={{ color: PALETTE.inchworm, fontSize: 14, margin: 0 }}>
                by {campaignInfo?.brand_name}
              </p>
              <button
                onClick={() => setShowBriefingSheet(true)}
                style={{
                  background: 'transparent',
                  border: `1.5px solid rgba(255,255,255,0.4)`,
                  color: '#fff',
                  borderRadius: 99,
                  padding: '3px 10px',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                View Briefing
              </button>
            </div>
          </div>
          <a
            href="https://brandcomply.ai"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 10,
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              flexShrink: 0,
              alignSelf: 'flex-end',
            }}
          >
            powered by <span style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>BrandComply</span>
          </a>
        </div>
      </div>

      {/* Step indicator — matches campaign creation wizard in BrandCampaignManagerMobile */}
      <div style={{ display: 'flex', gap: 8, padding: '16px 20px', background: '#fff' }}>
        {['Contact Info', 'Video Upload', 'Analysis'].map((label, idx) => {
          const stepMap: Record<string, number> = { info: 0, pending: 0, upload: 1, preview: 1, analyzing: 2 };
          const currentIdx = stepMap[step] ?? 0;
          const isActive = currentIdx === idx;
          const isCompleted = currentIdx > idx;
          return (
            <React.Fragment key={label}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  background: isActive ? PALETTE.gunmetal : isCompleted ? PALETTE.inchworm : '#e0e0e0',
                  color: isActive ? '#fff' : PALETTE.gunmetal,
                }}>
                  {isCompleted ? <Check size={14} /> : idx + 1}
                </div>
                <span style={{ fontSize: 11, color: isActive ? PALETTE.gunmetal : '#999', fontWeight: isActive ? 600 : 400 }}>
                  {label}
                </span>
              </div>
              {idx < 2 && (
                <div style={{
                  flex: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  paddingBottom: 18,
                }}>
                  <div style={{
                    height: 2,
                    width: '100%',
                    background: isCompleted ? PALETTE.inchworm : '#e0e0e0',
                    borderRadius: 1,
                  }} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', overflowY: 'auto' }}>

        {/* INFO STEP: Collect creator details */}
        {step === 'info' && (
          <div className="mobile-animate-fade-in">
            <h2 style={{ fontSize: 18, marginBottom: 8, color: PALETTE.gunmetal }}>
              Welcome to your pre-submission self-check!
            </h2>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
              We help you catch missing requirements instantly so you can submit with confidence, and avoid revisions.
            </p>

            <div className="mobile-input-group">
              <label className="mobile-input-label">First Name <span style={{ color: PALETTE.orange }}>*</span></label>
              <input
                type="text"
                className="mobile-input"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Surname <span style={{ color: PALETTE.orange }}>*</span></label>
              <input
                type="text"
                className="mobile-input"
                placeholder="Enter your surname"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
              />
            </div>

            <div className="mobile-input-group">
              <label className="mobile-input-label">Email Address <span style={{ color: PALETTE.orange }}>*</span></label>
              <input
                type="email"
                className="mobile-input"
                placeholder="your@email.com"
                value={creatorEmail}
                onChange={(e) => {
                  setCreatorEmail(e.target.value);
                  setEmailError('');
                }}
                style={emailError ? { borderColor: '#dc2626' } : {}}
              />
              {emailError && (
                <p style={{ color: '#dc2626', fontSize: 13, margin: '4px 0 0' }}>{emailError}</p>
              )}
            </div>

            {error && (
              <div style={{ 
                background: '#fef2f2', 
                padding: 12, 
                borderRadius: 8, 
                marginTop: 16,
                color: '#dc2626',
                fontSize: 14
              }}>
                {error}
              </div>
            )}

            {!canAnalyze ? (
              <div style={{
                background: '#FEF3C7',
                border: '1px solid #F59E0B',
                borderRadius: 12,
                padding: 14,
                marginTop: 24,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}>
                <AlertCircle size={18} color="#D97706" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, margin: 0 }}>
                    Submissions paused
                  </p>
                  <p style={{ fontSize: 12, color: `${PALETTE.gunmetal}cc`, margin: '4px 0 0' }}>
                    The brand's video check limit has been reached for this month. Please try again later or contact the brand directly.
                  </p>
                </div>
              </div>
            ) : (
              <button
                className="mobile-btn mobile-btn-primary mobile-btn-full"
                onClick={handleCheckSubmission}
                disabled={isCheckingSubmission || !firstName.trim() || !surname.trim() || !creatorEmail.trim()}
                style={{ marginTop: 24 }}
              >
                {isCheckingSubmission ? (
                  <>
                    <Loader2 size={18} className="mobile-spinner" style={{ width: 18, height: 18, marginRight: 8 }} />
                    Checking...
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            )}
          </div>
        )}

        {/* PENDING STEP: Show pending analysis */}
        {step === 'pending' && checkResult?.pending_analysis && (
          <div className="mobile-animate-fade-in">
            <h2 style={{ fontSize: 18, marginBottom: 8, color: PALETTE.gunmetal }}>
              Pending Analysis Found
            </h2>
            <p style={{ fontSize: 14, color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
              You have a video that was analyzed but not yet submitted. Would you like to view it or upload a new one?
            </p>

            {checkResult.pending_analysis.score !== null && (
              <div style={{
                background: '#f9f9f9',
                borderRadius: 12,
                padding: 16,
                marginBottom: 20,
                textAlign: 'center',
              }}>
                <p style={{ color: '#666', fontSize: 13, margin: '0 0 8px' }}>Previous Score</p>
                <span style={{
                  fontSize: 28,
                  fontWeight: 700,
                  color: (checkResult.pending_analysis.score || 0) >= 90 ? '#16a34a' :
                         (checkResult.pending_analysis.score || 0) >= 61 ? PALETTE.orange : '#dc2626',
                }}>
                  {Math.round(checkResult.pending_analysis.score || 0)}%
                </span>
              </div>
            )}

            {/* Show existing submitted versions if any */}
            {checkResult.exists && checkResult.all_versions.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <button
                  onClick={() => setShowVersions(!showVersions)}
                  style={{
                    width: '100%',
                    background: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                    borderRadius: 12,
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    fontSize: 14,
                    color: PALETTE.gunmetal,
                  }}
                >
                  <span>Previous Submissions ({checkResult.version_count})</span>
                  {showVersions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showVersions && (
                  <div style={{ marginTop: 8 }}>
                    {checkResult.all_versions.map((v) => (
                      <div
                        key={v.analysis_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 16px',
                          background: '#fff',
                          border: '1px solid #e0e0e0',
                          borderRadius: 8,
                          marginBottom: 6,
                          fontSize: 13,
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>V{v.version}</span>
                        <span style={{ color: '#666' }}>
                          {v.score !== null ? `${Math.round(v.score)}%` : '-'}
                        </span>
                        <span style={{ color: '#999', fontSize: 12 }}>
                          {new Date(v.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="mobile-btn mobile-btn-outline"
                onClick={handleDiscardPending}
                style={{ flex: 1 }}
              >
                Upload New
              </button>
              <button
                className="mobile-btn mobile-btn-primary"
                onClick={handleContinuePending}
                style={{ flex: 1 }}
              >
                View Result
              </button>
            </div>
          </div>
        )}

        {/* UPLOAD STEP */}
        {step === 'upload' && (
          <div className="mobile-animate-fade-in">
            <h2 style={{ fontSize: 18, marginBottom: 8, color: PALETTE.gunmetal }}>
              Upload Your Video
              {checkResult?.exists && checkResult.next_version > 1 && (
                <span style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: PALETTE.orange,
                  background: `${PALETTE.orange}15`,
                  padding: '2px 10px',
                  borderRadius: 99,
                  marginLeft: 8,
                  verticalAlign: 'middle',
                }}>
                  V{checkResult.next_version}
                </span>
              )}
            </h2>

            {checkResult?.exists && (
              <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                This will be version {checkResult.next_version} of your submission.
              </p>
            )}

            {/* Upload Zone */}
            <div 
              className="mobile-upload-zone"
              onClick={() => fileInputRef.current?.click()}
              style={{ marginTop: 8 }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <Video size={48} className="mobile-upload-zone-icon" />
              <p className="mobile-upload-zone-text">Tap to select video</p>
              <p className="mobile-upload-zone-hint">MP4, MOV, or WebM • Max 500MB</p>
            </div>

            {error && (
              <div style={{ 
                background: '#fef2f2', 
                padding: 12, 
                borderRadius: 8, 
                marginTop: 16,
                color: '#dc2626',
                fontSize: 14
              }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && videoPreviewUrl && (
          <div className="mobile-animate-fade-in">
            <div style={{
              aspectRatio: '9/16',
              maxHeight: 400,
              background: '#000',
              borderRadius: 12,
              marginBottom: 20,
              marginLeft: 'auto',
              marginRight: 'auto',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <video
                ref={videoRef}
                src={videoPreviewUrl}
                controls
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>

            {error && (
              <div style={{ 
                background: '#fef2f2', 
                padding: 12, 
                borderRadius: 8, 
                marginBottom: 16,
                color: '#dc2626',
                fontSize: 14
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="mobile-btn mobile-btn-outline"
                onClick={handleReset}
                style={{ flex: 1 }}
              >
                <RotateCcw size={18} style={{ marginRight: 8 }} />
                Change
              </button>
              <button
                className="mobile-btn mobile-btn-primary"
                onClick={handleAnalyze}
                style={{ flex: 2 }}
              >
                <Play size={18} style={{ marginRight: 8 }} />
                Check Video
              </button>
            </div>
          </div>
        )}

        {/* Analyzing Step */}
        {step === 'analyzing' && (
          <div className="mobile-animate-fade-in" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minHeight: 300,
          }}>
            <div style={{ position: 'relative', width: 120, height: 120 }}>
              <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                <circle
                  cx="60" cy="60" r="52"
                  fill="none"
                  stroke={`${PALETTE.americanSilver}40`}
                  strokeWidth="8"
                />
                <circle
                  cx="60" cy="60" r="52"
                  fill="none"
                  stroke={PALETTE.inchworm}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 52}
                  strokeDashoffset={2 * Math.PI * 52 * (1 - progress / 100)}
                  style={{ transition: 'stroke-dashoffset 0.4s ease' }}
                />
              </svg>
              <span style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                fontWeight: 700,
                color: PALETTE.inchworm,
              }}>
                {progress}%
              </span>
            </div>
          </div>
        )}

        {/* Result step removed — analysis now navigates directly to VideoResultMobile */}
      </div>

      {/* ============ BRIEFING DETAIL SHEET ============ */}
      <MobileSheet
        isOpen={showBriefingSheet}
        onClose={() => setShowBriefingSheet(false)}
        title="Campaign Briefing"
        fullScreen
      >
        {campaignInfo?.requirements ? (
          <div>
            {/* Copy Briefing button */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <button
                onClick={handleCopyBriefing}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: copiedBriefing ? `${PALETTE.inchworm}30` : '#f5f5f5',
                  border: `1px solid ${copiedBriefing ? PALETTE.inchworm : '#e0e0e0'}`,
                  borderRadius: 99,
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: PALETTE.gunmetal,
                  cursor: 'pointer',
                }}
              >
                {copiedBriefing ? <Check size={14} /> : <Copy size={14} />}
                {copiedBriefing ? 'Copied!' : 'Copy Briefing'}
              </button>
            </div>

            {/* Campaign Info */}
            <div style={{ background: `${PALETTE.americanSilver}10`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
              {campaignInfo.brand_name && (
                <div style={{ marginBottom: campaignInfo.product_name ? 16 : 0 }}>
                  <p style={{ fontSize: 11, fontWeight: 500, color: `${PALETTE.gunmetal}99`, margin: '0 0 2px' }}>Brand</p>
                  <p style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, margin: 0 }}>{campaignInfo.brand_name}</p>
                  {campaignInfo.brand_logo_path && (
                    <div style={{ marginTop: 10, padding: 8, borderRadius: 8, border: `1px solid ${PALETTE.americanSilver}60`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                      <img
                        src={campaignInfo.brand_logo_path}
                        alt="Brand Logo"
                        style={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              )}
              {campaignInfo.product_name && (
                <div>
                  <p style={{ fontSize: 11, fontWeight: 500, color: `${PALETTE.gunmetal}99`, margin: '0 0 2px' }}>Product</p>
                  <p style={{ fontSize: 14, fontWeight: 600, color: PALETTE.gunmetal, margin: 0 }}>{campaignInfo.product_name}</p>
                  {campaignInfo.product_image_path && (
                    <div style={{ marginTop: 10, padding: 8, borderRadius: 8, border: `1px solid ${PALETTE.americanSilver}60`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                      <img
                        src={campaignInfo.product_image_path}
                        alt="Product Image"
                        style={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Requirements */}
            {(() => {
              const requirementsData = campaignInfo.requirements;

              if (Array.isArray(requirementsData)) {
                const validReqs = requirementsData.filter((r: any) => r && typeof r === 'string' && r.trim());
                if (validReqs.length === 0) {
                  return <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requirements available</p>;
                }
                return (
                  <div style={{ background: `${PALETTE.americanSilver}10`, borderRadius: 12, padding: 16 }}>
                    <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: PALETTE.gunmetal }}>
                      All Requirements ({validReqs.length})
                    </h4>
                    {validReqs.map((req: string, idx: number) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                        <span style={{ flexShrink: 0, marginTop: 7, width: 4, height: 4, borderRadius: '50%', background: PALETTE.gunmetal, display: 'inline-block' }} />
                        <span style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.gunmetal }}>{req}</span>
                      </div>
                    ))}
                  </div>
                );
              }

              if (typeof requirementsData === 'object' && requirementsData !== null) {
                const categoryDisplayNames: { [key: string]: string } = {
                  'hooks': 'Hooks',
                  'topics': 'Topics',
                  'call_to_actions': 'Call to Actions',
                  'elements': "Don'ts",
                  'story': 'Story',
                  'visuals_background': 'Visuals & Background'
                };
                const categoryOrder = ['hooks', 'topics', 'call_to_actions', 'elements', 'story', 'visuals_background'];
                const categories = categoryOrder
                  .map(key => ({ key, value: requirementsData[key] }))
                  .filter(({ value }) => {
                    if (Array.isArray(value)) return value.some((item: any) => item && typeof item === 'string' && item.trim());
                    if (typeof value === 'string') return value.trim().length > 0;
                    return false;
                  });

                if (categories.length === 0) {
                  return <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requirements available</p>;
                }

                return (
                  <div>
                    {categories.map(({ key, value }) => {
                      const displayName = categoryDisplayNames[key] || key;
                      const isTextCategory = key === 'story' || key === 'visuals_background';
                      return (
                        <div key={key} style={{ background: `${PALETTE.americanSilver}10`, borderRadius: 12, padding: 16, marginBottom: 12 }}>
                          {Array.isArray(value) ? (
                            <>
                              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: PALETTE.gunmetal }}>
                                {displayName} ({value.filter((i: any) => i && i.trim()).length})
                              </h4>
                              {value.filter((item: any) => item && item.trim()).map((item: string, idx: number) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                                  <span style={{ flexShrink: 0, marginTop: 7, width: 4, height: 4, borderRadius: '50%', background: PALETTE.gunmetal, display: 'inline-block' }} />
                                  <span style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.gunmetal }}>{item}</span>
                                </div>
                              ))}
                            </>
                          ) : typeof value === 'string' && isTextCategory ? (
                            <>
                              <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: PALETTE.gunmetal }}>
                                {displayName}
                              </h4>
                              <p style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.gunmetal, margin: 0 }}>
                                {value}
                              </p>
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              return <p style={{ color: '#666', textAlign: 'center', padding: 20 }}>No requirements available</p>;
            })()}
          </div>
        ) : (
          <p style={{ color: '#666', textAlign: 'center', padding: 40 }}>No briefing available.</p>
        )}
      </MobileSheet>
    </div>
  );
};

export default CreatorCampaignUploadMobile;
